import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking");
  const [orderNumber, setOrderNumber] = useState(null);
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (attempts.current >= 8) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setOrderNumber(data.order_number);
          if (!cleared.current) { clear(); cleared.current = true; }
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* transient error — will retry */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="payment-success-page">
      {state === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="w-14 h-14 mx-auto text-primary animate-spin" />
          <h1 className="mt-5 text-2xl font-display font-black tracking-tight">Confirming your payment…</h1>
          <p className="mt-2 text-muted-foreground">Please wait a moment, don't close this page.</p>
        </div>
      )}

      {state === "paid" && (
        <div data-testid="payment-confirmed">
          <CheckCircle2 className="w-16 h-16 mx-auto text-primary" />
          <h1 className="mt-5 text-3xl font-display font-black tracking-tighter">Order confirmed!</h1>
          <p className="mt-3 text-muted-foreground">
            Thank you — your payment was successful{orderNumber ? <> and your order <span className="font-mono font-semibold text-foreground">{orderNumber}</span> is being processed.</> : "."}
          </p>
          <div className="mt-8 flex gap-3 justify-center">
            <Link to="/account" className="bg-primary text-white px-6 py-3 rounded-sm font-semibold hover:bg-primary/90">View my orders</Link>
            <Link to="/" className="border border-border px-6 py-3 rounded-sm font-semibold hover:bg-muted">Keep shopping</Link>
          </div>
        </div>
      )}

      {(state === "failed" || state === "error" || state === "timeout") && (
        <div data-testid="payment-issue">
          <AlertTriangle className="w-14 h-14 mx-auto text-destructive" />
          <h1 className="mt-5 text-2xl font-display font-black tracking-tight">
            {state === "timeout" ? "Still confirming your payment" : "We couldn't confirm your payment"}
          </h1>
          <p className="mt-2 text-muted-foreground">
            {state === "timeout"
              ? "Your payment may still be processing. Check your order history in a few minutes."
              : "If you were charged, please contact us and we'll sort it out right away."}
          </p>
          <div className="mt-8 flex gap-3 justify-center">
            <Link to="/account" className="bg-primary text-white px-6 py-3 rounded-sm font-semibold hover:bg-primary/90">View my orders</Link>
            <Link to="/contact" className="border border-border px-6 py-3 rounded-sm font-semibold hover:bg-muted">Contact us</Link>
          </div>
        </div>
      )}
    </div>
  );
}
